import { useState } from "react";
import { useTranslation } from "react-i18next";

const FEATURES = [
  {
    icon: "ri-chat-3-line",
    color: "#00D2A8",
    titleKey: "feat.msg.title",
    descKey: "feat.msg.desc",
  },
  {
    icon: "ri-vidicon-line",
    color: "#F7C948",
    titleKey: "feat.call.title",
    descKey: "feat.call.desc",
  },
  {
    icon: "ri-group-3-line",
    color: "#A78BFA",
    titleKey: "feat.group.title",
    descKey: "feat.group.desc",
  },
  {
    icon: "ri-file-transfer-line",
    color: "#F97316",
    titleKey: "feat.file.title",
    descKey: "feat.file.desc",
  },
  {
    icon: "ri-shield-keyhole-line",
    color: "#34D399",
    titleKey: "feat.encrypt.title",
    descKey: "feat.encrypt.desc",
  },
  {
    icon: "ri-notification-3-line",
    color: "#60A5FA",
    titleKey: "feat.notify.title",
    descKey: "feat.notify.desc",
  },
  {
    icon: "ri-computer-line",
    color: "#F472B6",
    titleKey: "feat.cross.title",
    descKey: "feat.cross.desc",
  },
  {
    icon: "ri-lock-2-line",
    color: "#FBBF24",
    titleKey: "feat.private.title",
    descKey: "feat.private.desc",
  },
  {
    icon: "ri-server-line",
    color: "#818CF8",
    titleKey: "feat.selfhost.title",
    descKey: "feat.selfhost.desc",
  },
];

const SHOWCASES = [
  {
    id: "messaging",
    icon: "ri-chat-3-line",
    color: "#00D2A8",
    titleKey: "showcase.msg.title",
    tagKey: "showcase.msg.tag",
    descKey: "showcase.msg.desc",
    pointKeys: ["showcase.msg.p1", "showcase.msg.p2", "showcase.msg.p3", "showcase.msg.p4"],
    image:
      "https://readdy.ai/api/search-image?query=modern%20instant%20messaging%20mobile%20app%20interface%20dark%20background%20teal%20green%20accent%20color%20chat%20bubbles%20text%20messages%20voice%20messages%20emoji%20reactions%20unread%20notifications%20clean%20minimal%20UI%20design%20with%20message%20list%20sidebar&width=560&height=380&seq=showcase-msg-1&orientation=landscape",
  },
  {
    id: "call",
    icon: "ri-vidicon-line",
    color: "#F7C948",
    titleKey: "showcase.call.title",
    tagKey: "showcase.call.tag",
    descKey: "showcase.call.desc",
    pointKeys: ["showcase.call.p1", "showcase.call.p2", "showcase.call.p3", "showcase.call.p4"],
    image:
      "https://readdy.ai/api/search-image?query=video%20call%20app%20interface%20dark%20background%20two%20people%20video%20conference%20HD%20quality%20screen%20sharing%20participants%20grid%20view%20audio%20mute%20video%20off%20buttons%20clean%20modern%20UI%20design&width=560&height=380&seq=showcase-call-1&orientation=landscape",
  },
  {
    id: "group",
    icon: "ri-group-3-line",
    color: "#A78BFA",
    titleKey: "showcase.group.title",
    tagKey: "showcase.group.tag",
    descKey: "showcase.group.desc",
    pointKeys: ["showcase.group.p1", "showcase.group.p2", "showcase.group.p3", "showcase.group.p4"],
    image:
      "https://readdy.ai/api/search-image?query=group%20chat%20app%20interface%20dark%20background%20purple%20accent%20multiple%20users%20avatars%20group%20conversation%20thread%20admin%20panel%20member%20list%20modern%20clean%20UI%20design%20messaging&width=560&height=380&seq=showcase-group-1&orientation=landscape",
  },
  {
    id: "security",
    icon: "ri-shield-keyhole-line",
    color: "#34D399",
    titleKey: "showcase.security.title",
    tagKey: "showcase.security.tag",
    descKey: "showcase.security.desc",
    pointKeys: [
      "showcase.security.p1",
      "showcase.security.p2",
      "showcase.security.p3",
      "showcase.security.p4",
    ],
    image:
      "https://readdy.ai/api/search-image?query=cybersecurity%20encryption%20technology%20abstract%20digital%20lock%20shield%20green%20neon%20dark%20background%20data%20protection%20privacy%20secure%20connection%20network%20security%20minimal%20tech%20illustration&width=560&height=380&seq=showcase-security-1&orientation=landscape",
  },
];

export default function FeaturesSection() {
  const { t } = useTranslation();
  const [activeShowcase, setActiveShowcase] = useState(0);

  const current = SHOWCASES[activeShowcase];

  return (
    <section id="features" className="py-24" style={{ backgroundColor: "var(--bg-base)" }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: "var(--accent)" }}
          >
            {t("features.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-extrabold mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            {t("features.title")}
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            {t("features.subtitle")}
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-24">
          {FEATURES.map((feat) => (
            <div
              key={feat.titleKey}
              className="group rounded-2xl p-6 transition-all cursor-default border"
              style={{
                backgroundColor: "var(--bg-surface)",
                borderColor: "var(--border-color)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--border-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-color)")}
            >
              <div
                className="w-11 h-11 flex items-center justify-center rounded-xl mb-4 transition-transform group-hover:scale-105"
                style={{ backgroundColor: `${feat.color}18` }}
              >
                <i className={`${feat.icon} text-xl`} style={{ color: feat.color }} />
              </div>
              <h3 className="text-base font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
                {t(feat.titleKey)}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {t(feat.descKey)}
              </p>
            </div>
          ))}
        </div>

        {/* ── Feature Showcase ── */}
        <div className="mb-4 text-center">
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: "var(--accent)" }}
          >
            {t("showcase.label")}
          </p>
          <h2
            className="text-3xl md:text-4xl font-extrabold mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            {t("showcase.title")}
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            {t("showcase.subtitle")}
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-wrap justify-center gap-2 mb-12 mt-8">
          {SHOWCASES.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => setActiveShowcase(idx)}
              className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border"
              style={
                activeShowcase === idx
                  ? {
                      backgroundColor: item.color,
                      borderColor: item.color,
                      color: "#0d1117",
                    }
                  : {
                      backgroundColor: "transparent",
                      borderColor: "var(--border-color)",
                      color: "var(--text-secondary)",
                    }
              }
              onMouseEnter={(e) => {
                if (activeShowcase !== idx) {
                  (e.currentTarget as HTMLElement).style.borderColor = item.color;
                  (e.currentTarget as HTMLElement).style.color = item.color;
                }
              }}
              onMouseLeave={(e) => {
                if (activeShowcase !== idx) {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                  (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                }
              }}
            >
              <i className={`${item.icon} text-base`} />
              {t(item.titleKey)}
            </button>
          ))}
        </div>

        {/* Showcase content */}
        <div
          key={current.id}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center rounded-3xl p-8 md:p-12 border"
          style={{
            backgroundColor: "var(--bg-surface)",
            borderColor: "var(--border-color)",
          }}
        >
          {/* Left: description */}
          <div>
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-5"
              style={{
                backgroundColor: `${current.color}18`,
                color: current.color,
              }}
            >
              <i className={`${current.icon}`} />
              {t(current.tagKey)}
            </span>

            <h3
              className="text-2xl md:text-3xl font-extrabold mb-4 leading-snug"
              style={{ color: "var(--text-primary)" }}
            >
              {t(current.descKey)}
            </h3>

            <ul className="space-y-3 mt-6">
              {current.pointKeys.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full mt-0.5 shrink-0"
                    style={{ backgroundColor: `${current.color}20` }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: current.color }} />
                  </div>
                  <span className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {t(key)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: screenshot */}
          <div className="w-full h-[280px] md:h-[340px] rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
            <img
              src={current.image}
              alt={t(current.titleKey)}
              className="w-full h-full object-cover object-top transition-all duration-500"
            />
          </div>
        </div>
      </div>
    </section>
  );
}